/** Reglas de precio según a quién se le vende.
 *
 *  NovaBerry maneja dos listas: la de empresas (pastelerías, hoteles,
 *  restoranes) y la de particulares. Vive acá y no dentro de una
 *  pantalla porque la usan tres lugares distintos —la venta, el
 *  simulador y la ficha del producto— y tienen que dar siempre el
 *  mismo número.
 *
 *  Todos los precios guardados son NETOS, sin IVA. El IVA se calcula
 *  solo para mostrarlo cuando el cliente es empresa y pide factura. */
import type { Producto, TipoCliente } from '../types'

/** Kilos reales de una línea. Solo se puede convertir a peso cuando el
 *  producto se vende por kilo o por gramo —el resto (unidad, litro,
 *  bandeja, caja) no cuenta para el umbral de volumen porque no hay
 *  forma confiable de saber cuánto pesa. */
export function kilosLinea(cantidad: number, producto: Producto): number {
  if (producto.unidad === 'kg') return cantidad * producto.peso
  if (producto.unidad === 'g') return (cantidad * producto.peso) / 1000
  return 0
}

/** Una línea de empresa entra al tramo de volumen cuando llega a 10
 *  kilos de ese mismo producto. */
export function esVolumen(
  cantidad: number,
  producto: Producto,
  tipo: TipoCliente | undefined,
): boolean {
  return (
    tipo === 'empresa' &&
    kilosLinea(cantidad, producto) >= 10 &&
    !!producto.precioMinimoMayoristaVolumen
  )
}

/** El piso de venta que le corresponde a esta línea: a una empresa que
 *  pide 10 kilos o más de un mismo producto le toca el precio por
 *  volumen (más barato); si no, el mayorista normal. A un particular
 *  siempre le toca el precio al detalle, sin importar cuánto pida. */
export function precioMinimoLinea(
  cantidad: number,
  producto: Producto,
  tipo: TipoCliente | undefined,
): number | undefined {
  // Ojo con el `?? undefined`: la base devuelve null cuando la columna
  // está vacía, y un null que se cuela más adelante se muestra como
  // "mínimo $0" —que se lee como si se pudiera regalar el producto.
  if (tipo === 'empresa') {
    if (esVolumen(cantidad, producto, tipo)) return producto.precioMinimoMayoristaVolumen ?? undefined
    return producto.precioMinimoMayorista ?? producto.precioMinimoDetalle ?? undefined
  }
  // A un particular NO se le aplica el piso mayorista como respaldo:
  // sería mostrarle al vendedor el precio de pastelería como si fuera
  // el mínimo de una venta al detalle, que es bastante más caro. Si no
  // hay piso de detalle cargado, sencillamente no hay piso.
  return producto.precioMinimoDetalle ?? undefined
}

/** El precio con el que se propone la línea al agregarla al carro: el
 *  de la lista que le toca a ese tipo de cliente. Si el producto no
 *  tiene precio mayorista cargado se cae al sugerido —vender igual es
 *  mejor que quedar en cero—, y `tienePrecioPropio` avisa de eso para
 *  poder marcarlo en pantalla. */
export function precioLista(
  producto: Producto,
  tipo: TipoCliente,
  cantidad = 1,
): number {
  if (tipo === 'empresa') {
    if (esVolumen(cantidad, producto, tipo)) return producto.precioMinimoMayoristaVolumen!
    return producto.precioMinimoMayorista ?? producto.precioSugerido
  }
  return producto.precioMinimoDetalle ?? producto.precioSugerido
}

/** ¿Este producto tiene precio propio para esa lista, o se está usando
 *  el sugerido como reemplazo? */
export function tienePrecioPropio(producto: Producto, tipo: TipoCliente): boolean {
  return tipo === 'empresa'
    ? producto.precioMinimoMayorista !== undefined && producto.precioMinimoMayorista !== null
    : true
}

/** Neto → total con IVA, redondeado al peso. */
export function conIva(neto: number, porcentajeIva: number): number {
  return Math.round(neto * (1 + porcentajeIva / 100))
}

/** La parte de IVA de un neto, redondeada al peso. */
export function montoIva(neto: number, porcentajeIva: number): number {
  return conIva(neto, porcentajeIva) - neto
}
